import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AppListing } from "../../components/appListing";
import { visitSummariesApi } from "../../redux/service/visitSummaries_service";
import { toast } from "react-toastify";
import { IoMdEye } from "react-icons/io";
import moment from "moment";
import MainPageDialog from "../../components/mainPageDialog";
import { getFileUrl } from "../care_plan/constant";
import { getBaseUrl, LoadingData } from "../../utils/common_lib";
import { ALLOW_DOWNLOAD_VISIT } from "../../constants/policies";

function VisitSummaries() {
  const [visitSummariesListingTrigger] =
    visitSummariesApi.endpoints.getVisitSummaries.useLazyQuery();

  const [printVisitSummaryTrigger] =
    visitSummariesApi.endpoints.printSummary.useLazyQuery();

  const [loading, setLoading] = useState(false);
  const [loadingPdfView, setLoadingPdfView] = useState(false);
  const [visitSummaries, setVisitSummaries] = useState([]);

  const [showSummaryModal, setShowSummaryModal] = useState({
    show: false,
    uuid: "",
    summary_html: "",
  });

  const authToken = localStorage.getItem("authToken") || JSON.parse(localStorage.getItem("session"))?.token || null;
  const practiceId = JSON.parse(localStorage.getItem("session"))?.practice_id || null;

  const handleExternalDownloadFile = useCallback((item) => {
    if(showSummaryModal?.uuid) {
      let url = `${getBaseUrl()}/utilities/get_attachment.json?attachment=${showSummaryModal?.uuid}&token=${authToken}&tz=-300&download=1`;
      window.open(url, "_blank");
    } else {
      toast.error("Unable to download file");
    }
  }, [showSummaryModal]);

  const fetchVisitSummaries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await visitSummariesListingTrigger({
        patient_portal: 1,
        status: "signed",
      });
      if (response?.data?.data) {
        setVisitSummaries(response.data.data);
      } else {
        setVisitSummaries([]);
      }
    } catch (error) {
      console.error("Error fetching visitSummaries", error);
      toast.error("Error fetching visitSummaries");
      setVisitSummaries([]);
    } finally {
      setLoading(false);
    }
  }, [visitSummariesListingTrigger]);

  useEffect(() => {
    fetchVisitSummaries();
  }, [fetchVisitSummaries]);

  const handlePrintSummary = useCallback(
    (item) => {
      setLoadingPdfView(true);
      printVisitSummaryTrigger({
        encounter_id: item.encounter,
        patient_portal: 1,
        preview_html: 1,
      })
        .then((response) => {
          setShowSummaryModal({
            show: true,
            uuid: response?.data?.data?.pdf?.uuid,
            summary_html: response?.data?.data?.pdf?.html,
          });
        })
        .catch((error) => {
          console.error("Error fetching visit summary pdf", error);
          toast.error("Error fetching visit summary pdf");
        })
        .finally(() => {
          setLoadingPdfView(false);
        });
    },
    [printVisitSummaryTrigger]
  );

  const columns = useMemo(
    () => [
      {
        title: "Encounter Date",
        key: "date",
        getData: (item) => moment(item.date).format("MM/DD/YYYY hh:mm A"),
      },
      { title: "Visit Reason", key: "visit_reason" },
      { title: "Encounter Type", key: "encounter_type_title" },
      { title: "Provider", key: "provider_name" },
      {
        title: "Facility",
        key: "facility",
        getData: (item) => item?.Location?.name,
      },
      {
        title: "Status",
        key: "status",
        getData: (item) => <span className="capitalize">{item?.status}</span>,
      },
      {
        title: "Preview",
        key: "",
        getData: (item) => (
          <div
            className="flex w-full items-center justify-center px-2"
            role="button"
            tabIndex={0}
            onClick={() => handlePrintSummary(item)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handlePrintSummary(item);
              }
            }}
          >
            <IoMdEye className="cursor-pointer" color="#0070e5" />
          </div>
        ),
      },
    ],
    [handlePrintSummary]
  );

  const footerList = [
    {
      title: "Close",
      type: "cancel",
      action: () => {
        setShowSummaryModal(false);
      },
    },
    ((ALLOW_DOWNLOAD_VISIT || [])?.includes(practiceId) && {
      title: "Download",
      type: "save",
      action: handleExternalDownloadFile,
    }),
  ];

  return (
    <>
      {(showSummaryModal?.show || loadingPdfView) && (
        <MainPageDialog
          title="Summary"
          modalSize="md"
          maxHeight={true}
          footerList={footerList}
          onCancelHandler={() =>
            setShowSummaryModal({
              show: false,
              uuid: "",
              summary_html: "",
            })
          }
        >
          {loadingPdfView ? (
            <LoadingData />
          ) : (
            <div>
              {showSummaryModal?.summary_html ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: showSummaryModal?.summary_html,
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: `<iframe id="printDiv" width="100%" height="1100" scrolling="auto" frameborder="1" allow="autoplay" src=${getFileUrl(
                      showSummaryModal?.uuid
                    )}></iframe>`,
                  }}
                />
              )}
            </div>
          )}
        </MainPageDialog>
      )}
      <div className="flex border-gray-300 bg-gray-200 px-3 py-3 sm:px-4 flex-wrap items-center justify-between sm:flex-nowrap">
        <div className="flex gap-5 items-center">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            Visit Summaries
          </h3>
        </div>
        <div className="flex-shrink-0">
          <button
            type="button"
            onClick={fetchVisitSummaries}
            className="relative inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Reload
          </button>
        </div>
      </div>
      <div className="flex-auto bg-white justify-start p-3 text-start">
        <AppListing
          loading={loading}
          listData={visitSummaries}
          cols={columns}
        />
      </div>
    </>
  );
}

export default React.memo(VisitSummaries);
